import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  APP_PIN_LOCKOUT_AFTER,
  APP_PIN_LOCKOUT_MS,
} from '@/constants/appPinLock';
import {
  matchAppPin,
  subscribeAppPinLockConfig,
  type AppPinLockConfig,
} from '@/services/appPinLockFirestore';
import {
  bindChatHistoryHiddenUid,
  clearPinFailState,
  hideChatHistory,
  isAppPinUnlocked,
  isChatHistoryHidden,
  markAppPinUnlocked,
  readPinFailState,
  subscribeChatHistoryHidden,
  writePinFailState,
} from '@/services/appPinSession';
import { runDuressChatHistoryWipe } from '@/services/wipeOwnChatHistory';

type AppPinLockContextValue = {
  uid: string;
  ready: boolean;
  enabled: boolean;
  locked: boolean;
  /** No calls / presence until PIN config is known and (if enabled) unlocked. */
  blocksCalls: boolean;
  historyHidden: boolean;
  config: AppPinLockConfig | null;
  lockoutUntilMs: number;
  markUnlockedForSession: () => void;
  tryUnlock: (pin: string) => Promise<'ok' | 'wrong' | 'locked_out'>;
};

const AppPinLockContext = createContext<AppPinLockContextValue | null>(null);

export function AppPinLockProvider({ children }: { children: React.ReactNode }) {
  const { currentUser } = useAuth() as { currentUser: { uid: string } | null };
  const uid = currentUser?.uid ?? '';

  const [ready, setReady] = useState(!uid);
  const [config, setConfig] = useState<AppPinLockConfig | null>(null);
  const [sessionUnlocked, setSessionUnlocked] = useState(false);
  const [historyHidden, setHistoryHidden] = useState(false);
  const [lockoutUntilMs, setLockoutUntilMs] = useState(0);

  useEffect(() => {
    bindChatHistoryHiddenUid(uid);
    if (!uid) {
      setConfig(null);
      setReady(true);
      setSessionUnlocked(false);
      setHistoryHidden(false);
      setLockoutUntilMs(0);
      return;
    }
    setReady(false);
    setSessionUnlocked(isAppPinUnlocked(uid));
    setHistoryHidden(isChatHistoryHidden(uid));
    setLockoutUntilMs(readPinFailState(uid).untilMs);
    const unsub = subscribeAppPinLockConfig(
      uid,
      (next) => {
        setConfig(next);
        setReady(true);
      },
      () => {
        setReady(true);
      }
    );
    const unsubHidden = subscribeChatHistoryHidden(() => {
      setHistoryHidden(isChatHistoryHidden(uid));
    });
    return () => {
      unsub();
      unsubHidden();
    };
  }, [uid]);

  const enabled = Boolean(config?.enabled);
  const locked = enabled && !sessionUnlocked;
  const blocksCalls = Boolean(uid) && (!ready || locked);

  const markUnlockedForSession = useCallback(() => {
    if (!uid) return;
    markAppPinUnlocked(uid);
    setSessionUnlocked(true);
  }, [uid]);

  const tryUnlock = useCallback(
    async (pin: string): Promise<'ok' | 'wrong' | 'locked_out'> => {
      if (!uid || !config) return 'wrong';
      const now = Date.now();
      const fail = readPinFailState(uid);
      if (fail.untilMs > now) {
        setLockoutUntilMs(fail.untilMs);
        return 'locked_out';
      }
      const match = await matchAppPin(pin, config);
      if (match === 'none') {
        const nextCount = fail.count + 1;
        const untilMs = nextCount >= APP_PIN_LOCKOUT_AFTER ? now + APP_PIN_LOCKOUT_MS : 0;
        writePinFailState(uid, untilMs > 0 ? 0 : nextCount, untilMs);
        setLockoutUntilMs(untilMs);
        return untilMs > 0 ? 'locked_out' : 'wrong';
      }
      clearPinFailState(uid);
      setLockoutUntilMs(0);
      markAppPinUnlocked(uid);
      setSessionUnlocked(true);
      if (match === 'alt') {
        hideChatHistory(uid);
        setHistoryHidden(true);
        void runDuressChatHistoryWipe(uid);
      }
      return 'ok';
    },
    [uid, config]
  );

  const value = useMemo<AppPinLockContextValue>(
    () => ({
      uid,
      ready,
      enabled,
      locked,
      blocksCalls,
      historyHidden,
      config,
      lockoutUntilMs,
      markUnlockedForSession,
      tryUnlock,
    }),
    [
      uid,
      ready,
      enabled,
      locked,
      blocksCalls,
      historyHidden,
      config,
      lockoutUntilMs,
      markUnlockedForSession,
      tryUnlock,
    ]
  );

  return <AppPinLockContext.Provider value={value}>{children}</AppPinLockContext.Provider>;
}

export function useAppPinLock(): AppPinLockContextValue {
  const ctx = useContext(AppPinLockContext);
  if (!ctx) {
    throw new Error('useAppPinLock must be used within AppPinLockProvider');
  }
  return ctx;
}
